import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { COLORS } from "assets/styles/colors";
import { PrimaryButton } from "common";
import { adminRoutes } from "router/routes";
import { singleEvidenceSelectors } from "store/admin-slices/single-evidence-slice/selectors";
import { singleEvidenceActions } from "store/admin-slices/single-evidence-slice/slice";
import { LOADING_STATUSES, RESIDENT_TYPES } from "utils/constants";
import { generateParams, isEmpty, numberToDollar } from "utils/helpers";

import { EvidenceSidebarDates } from "./EvidenceSidebarDates/EvidenceSidebarDates";
import { EvidenceSidebarFloats } from "./EvidenceSidebarFloats/EvidenceSidebarFloats";
import { EvidenceSidebarRanges } from "./EvidenceSidebarRanges/EvidenceSidebarRanges";
import { EvidenceSidebarSelects } from "./EvidenceSidebarSelects/EvidenceSidebarSelects";
import { useFilters } from "./hooks";
import * as S from "./styled";

export const EvidenceSideBar = () => {
  const dispatch = useDispatch();
  const {
    leftBarValues,
    subjectProperty,
    residentType,
    selects,
    ranges,
    floats,
    dates,
    sortData,
    useAlgorithm,
    appliedFilters,
  } = useSelector(singleEvidenceSelectors);

  const propertyCounty = leftBarValues?.county || "";

  const navigate = useNavigate();

  const property_id = useParams().propertyId;

  const {
    filters,
    numbers,
    datesValues,
    selectedOptions,
    setNumbers,
    handleSelectChange,
    handleFirstFilterChange,
    handleSecondFilterChange,
    handleDateChange,
    onApplyFilters,
  } = useFilters({ property_id, propertyCounty });

  const setGetEvidenceStatusSuccess = () => {
    residentType === RESIDENT_TYPES.equity
      ? dispatch(singleEvidenceActions.setGetEvidencesEquitiesStatus(LOADING_STATUSES.succeeded))
      : dispatch(singleEvidenceActions.setGetEvidencesSalesStatus(LOADING_STATUSES.succeeded));
  };

  // Get Data
  useEffect(() => {
    if (!isEmpty(appliedFilters)) {
      const params = generateParams({
        property_id,
        sortData,
        residentType,
        appliedFilters,
        selects,
        ranges,
        dates,
        floats,
      });

      if (
        propertyCounty === "Harris" &&
        !params?.neighborhood_group?.length &&
        !params?.neighborhood_code?.length &&
        !appliedFilters?.radius
      ) {
        toast.error("You need to fill at least Neighborhood code, Neighborhood group or Radius", { autoClose: 3000 });
        setGetEvidenceStatusSuccess();
      } else if (propertyCounty !== "Harris" && !params?.neighborhood_code?.length && !appliedFilters?.radius) {
        toast.error("You need to fill at least Neighborhood code or Radius", { autoClose: 3000 });
        setGetEvidenceStatusSuccess();
      } else {
        if (residentType === RESIDENT_TYPES.equity) {
          dispatch(singleEvidenceActions.getEvidencesEquities({ ...params, with_algorithm: useAlgorithm ? 1 : 0 }));
        } else if (residentType === RESIDENT_TYPES.sales) {
          dispatch(singleEvidenceActions.getEvidencesSales({ ...params, with_algorithm: useAlgorithm ? 1 : 0 }));
        }
      }
    }
  }, [appliedFilters]);

  useEffect(() => {
    return () => {
      dispatch(singleEvidenceActions.setAppliedFilters(null));
    };
  }, []);

  const handleApply = () => {
    if (
      propertyCounty === "Harris" &&
      !selectedOptions?.neighborhood_group?.length &&
      !selectedOptions?.neighborhood_code?.length &&
      !numbers?.radius
    ) {
      toast.error("You need to fill at least Neighborhood code, Neighborhood group or Radius", { autoClose: 3000 });
    } else if (propertyCounty !== "Harris" && !selectedOptions?.neighborhood_code?.length && !numbers?.radius) {
      toast.error("You need to fill at least Neighborhood code or Radius", { autoClose: 3000 });
    } else {
      onApplyFilters();
    }
  };

  const goBack = () => navigate(`${adminRoutes.prefix}/${adminRoutes.evidence}`);

  const propertyData = [
    {
      id: 1,
      label: "Account:",
      value: leftBarValues?.account_number,
    },
    {
      id: 2,
      label: "Situs:",
      value: leftBarValues?.street_address,
    },
    {
      id: 3,
      label: "Owner:",
      value: leftBarValues?.county_owner_name,
    },
    {
      id: 4,
      label: "County:",
      value: leftBarValues?.county,
    },
  ];

  const propertyInfo = [
    {
      label: new Date().getFullYear() + " Certified Value",
      value: leftBarValues?.certified_value,
      isMoney: true,
    },
    {
      label: "+/-",
      value: leftBarValues?.sales,
      isMoney: true,
    },
    {
      label: `Adjusted ${residentType === RESIDENT_TYPES.sales ? "Sales" : "Equity"} Value`,
      value: leftBarValues?.adjusted_sales_value,
      isMoney: true,
    },
  ];

  const { selectsFirstGroup, selectetsSecondGroup } = useMemo(() => {
    const initialValue = { selectsFirstGroup: [], selectetsSecondGroup: [] };
    if (!selects) {
      return initialValue;
    }

    return selects.reduce((acc, selectItem) => {
      if (!["exterior_wall", "foundation_type", "stories", "construction_type"].includes(selectItem.description)) {
        acc.selectsFirstGroup.push(selectItem);
      } else {
        acc.selectetsSecondGroup.push(selectItem);
      }

      return acc;
    }, initialValue);
  }, [selects]);

  return (
    <S.SlidebarContainer>
      <S.SiteBarFixedContainer>
        <S.StickyContainer>
          <PrimaryButton className="back-button" eventHandler={goBack} label="Back" />
          <S.Border />
          <S.PropertyParentContainer>
            {propertyData.map((item) => (
              <S.PropertyContainer key={item.id}>
                <S.Label>{item?.label}</S.Label>
                <S.Value>{item?.value}</S.Value>
              </S.PropertyContainer>
            ))}
          </S.PropertyParentContainer>
          <S.Border />
          <S.PropertyParentContainer>
            {propertyInfo.map((item) => (
              <S.PropertyContainer key={item.label}>
                <S.Label>{item.label}</S.Label>
                {leftBarValues ? (
                  <S.Value
                    style={
                      item.label === "+/-"
                        ? { color: COLORS.green_300 }
                        : item.label === "Adjusted Value"
                        ? { color: COLORS.light_blue }
                        : {}
                    }
                  >
                    {item.isMoney ? numberToDollar(item.value) : item.value}
                  </S.Value>
                ) : null}
              </S.PropertyContainer>
            ))}
          </S.PropertyParentContainer>
          <S.Border />
        </S.StickyContainer>
        <S.FilterContainer>
          <S.Heading>
            <S.HeadingText>SELECT COMPS BY:</S.HeadingText>
            <PrimaryButton eventHandler={handleApply} label="Apply" />
          </S.Heading>
          <S.InputsContainer>
            <EvidenceSidebarDates
              dates={dates}
              datesValues={datesValues}
              handleDateChange={handleDateChange}
              subjectProperty={{ ...subjectProperty, county: propertyCounty }}
            />
            <EvidenceSidebarSelects
              handleSelectChange={handleSelectChange}
              selectedOptions={selectedOptions}
              selects={selectsFirstGroup}
              subjectProperty={subjectProperty}
            />
            <EvidenceSidebarFloats floats={floats} numbers={numbers} setNumbers={setNumbers} />
          </S.InputsContainer>
        </S.FilterContainer>
        <S.Border />
        <S.FilterContainer>
          <S.Heading>
            <S.HeadingText>FILTER</S.HeadingText>
            <PrimaryButton eventHandler={handleApply} label="Apply" />
          </S.Heading>
          <EvidenceSidebarSelects
            handleSelectChange={handleSelectChange}
            selectedOptions={selectedOptions}
            selects={selectetsSecondGroup}
            subjectProperty={subjectProperty}
          />
          <EvidenceSidebarRanges
            filters={filters}
            handleFirstFilterChange={handleFirstFilterChange}
            handleSecondFilterChange={handleSecondFilterChange}
            ranges={ranges}
            subjectProperty={subjectProperty}
          />
        </S.FilterContainer>
      </S.SiteBarFixedContainer>
    </S.SlidebarContainer>
  );
};
